import { useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';
import { fetchPreviewBlob, type PreviewTarget } from '@/api/client';

/**
 * ============================================================================
 * ⭐ A GLIMPSE OF THE PAGE, BESIDE THE FILE NAME
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"The small icon of the image should be next the file
 * > name so they can see the glimpse but the eye icon pops it into a viewer."*
 *
 * ---- ⛔ IMAGES ONLY, AND THAT IS THE WHOLE REASON IT IS CHEAP -------------
 *
 * ⚠️ A PDF IS NOT A PICTURE — something has to draw it, which is the 69 MB of
 * renderer measured in §1AO and not shipped. ⭐ A JPEG or PNG **is already a
 * picture**, so the browser shrinks it for free with no library at all.
 *
 * ⛔ SO A PDF ROW GETS THE FILE ICON, deliberately, and that is not a
 * limitation to be worked around later by loading a PDF engine into the bundle.
 * It is why scanning to JPEG is worth asking the scanner people about.
 *
 * ---- ⚠️ TWO SOURCES, BECAUSE ONE OF THEM IS FREE -------------------------
 *
 *   `objectUrl` — a LOCAL scan. The browser already holds the file, so the
 *                 thumbnail costs one `URL.createObjectURL` and no network.
 *   `target`    — a STORED document. One fetch through the inline preview
 *                 route, then cached for the life of the mount.
 *
 * ⛔ NEVER BOTH. The local path exists precisely so the tray on a scanning
 * workstation does not pull every scan back down again to draw a 28px square.
 *
 * ---- ⛔ IT NEVER BLOCKS THE ROW ------------------------------------------
 *
 * ⚠️ A thumbnail that fails is a thumbnail that is absent, not a row that
 * breaks. Every failure path lands on the file icon, silently — this is
 * decoration, and the file name is the thing that actually identifies the
 * document.
 * ============================================================================
 */

/**
 * ---- ⛔⚤ NOTHING ASKS FOR A PICTURE ANY MORE (2026-09-23) -------------------
 *
 * > Pete: *"the thumbnail image of the doc is still flickering, please remove it from
 * > the scanned paperwork panels"* - then, on finding it still there: *"The thumbnail
 * > is not gone on paperwork either."*
 *
 * ⛔⛔ THE FIRST ATTEMPT SWAPPED ONE THUMBNAIL FOR ANOTHER. `LocalThumb` was deleted
 * from `ScanFolderPanel` and THIS component took its slot - which also draws a picture,
 * merely fetched instead of read locally. It happened to degrade to the glyph there
 * because that one call passed no `target`, and the accident was read as the fix.
 *   ** REMOVING THE COMPONENT THAT DREW A THING IS NOT REMOVING THE THING, IF ITS
 *      REPLACEMENT DRAWS THE SAME THING. **
 *
 * ⭐ SO THE CHANGE IS AT THE CALL SITES, NOT HERE: no caller passes `target` or
 * `objectUrl`, so the effect below returns immediately and every row renders the
 * file-type glyph. `paperwork-tray-guard` pins that - the rule is *no caller asks*,
 * which is checkable, rather than *the machinery is deleted*, which would also delete
 * the glitch fix recorded below.
 *
 * ⚠️ THE MACHINERY IS KEPT ON PURPOSE. It is the answer to a real bug (the object
 * literal dependency, below) and the place a future thumbnail would come back to. A
 * thumbnail that returns must answer the flicker first, and the workings of that answer
 * are here, tested, rather than in somebody's memory of a deleted file.
 * 🔨 The eye icon opens a real preview. That is where a picture belongs.
 */

/** ⭐ The extensions the browser can draw unaided. Deliberately not `previewTypes`' list: that one is a security boundary about iframes, this is "is it already a picture". */
export function isImageFile(fileName: string): boolean {
    return /\.(jpe?g|png|gif|webp|bmp)$/i.test((fileName ?? '').trim());
}

export function DocumentThumb({
    fileName, objectUrl, target, size = 28,
}: {
    fileName: string;
    /** ⭐ A local file the browser already has — free, no network. */
    objectUrl?: string | null;
    /** A stored document — fetched once through the preview route. */
    target?: PreviewTarget | null;
    size?: number;
}) {
    const [url, setUrl] = useState<string | null>(objectUrl ?? null);
    const [failed, setFailed] = useState(false);
    /** ⛔ Revoked on unmount, but only the one WE made — not the caller's. */
    const madeRef = useRef<string | null>(null);

    /**
     * ---- ⛔⛔ THE OBJECT IS NOT THE IDENTITY (2026-09-10) -------------------
     *
     * > Pete: *"the thumbnails are gliching because of the refresh, anyway to
     * > stop that?"*
     *
     * ⛔ `target` WAS A DEPENDENCY, AND EVERY CALLER BUILDS IT INLINE:
     * `target={{ kind: 'document', documentId: d.documentId }}`. A fresh object
     * literal on every render is a CHANGED dependency on every render — so each
     * time the tray re-rendered (a poll, a refresh, a parent state change) this
     * effect tore down, revoked the blob URL it was displaying, and refetched the
     * very same bytes. ⚠️ The picture blinked out and back each time.
     *
     * ⭐ THE KEY IS WHAT THE TARGET MEANS, not which object expressed it. Two
     * literals naming the same document produce the same string, so the effect
     * stops firing — and it still fires the moment a row genuinely points at a
     * different document.
     *
     * ⛔ NOT SOLVED BY `useMemo` AT EVERY CALL SITE. That is four components
     * remembering a discipline; this is one line where the bug actually lives.
     */
    const targetKey = target === null || target === undefined
        ? null
        : target.kind === 'document'
            ? `document:${target.documentId}`
            : `scan:${target.facilityCode}:${target.itemId}`;
    /* ⚠️ The effect needs the CURRENT target without depending on its identity. */
    const targetRef = useRef(target);
    targetRef.current = target;

    useEffect(() => {
        if (objectUrl) { setUrl(objectUrl); return; }
        const t = targetRef.current;
        if (!t || !isImageFile(fileName)) return;
        let cancelled = false;
        void (async () => {
            try {
                /* ⚠️ The token comes from the client module's own accessor here
                   rather than a prop: a thumbnail is decoration and must not
                   widen every row's prop contract to carry auth. */
                const { url: u } = await fetchPreviewBlob(await tokenFor(), t);
                if (cancelled) { URL.revokeObjectURL(u); return; }
                madeRef.current = u;
                setUrl(u);
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => {
            cancelled = true;
            if (madeRef.current) { URL.revokeObjectURL(madeRef.current); madeRef.current = null; }
        };
        /* ⛔ `targetKey`, NOT `target` — see the note above. Putting the object
           back reinstates the flash on every render, and `paperwork-tray-guard`
           asserts this line for that reason. */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUrl, targetKey, fileName]);

    const box = { width: size, height: size };

    if (!isImageFile(fileName) || failed || !url) {
        return (
            <span
                className="flex shrink-0 items-center justify-center rounded border bg-muted/40"
                style={box}
                aria-hidden
            >
                <FileText className="h-3 w-3 text-muted-foreground" />
            </span>
        );
    }

    return (
        <img
            src={url}
            alt=""
            aria-hidden
            onError={() => setFailed(true)}
            className="shrink-0 rounded border object-cover"
            style={box}
        />
    );
}

/**
 * ⚠️ SET ONCE BY THE APP SHELL. The alternative was threading `getToken` through
 * every tray row purely so a decorative square could authenticate, which would
 * have put auth plumbing in four components that have no other use for it.
 */
let tokenAccessor: (() => Promise<string>) | null = null;
export function setThumbTokenAccessor(fn: () => Promise<string>) { tokenAccessor = fn; }
async function tokenFor(): Promise<string> {
    if (!tokenAccessor) throw new Error('thumbnail token accessor not set');
    return tokenAccessor();
}
